<template>
  <div class="row g-4">
    <div class="col-12">
      <div
        class="d-flex flex-wrap justify-content-between align-items-center gap-2"
      >
        <div>
          <h5 class="mb-0">Where does net revenue come from?</h5>
          <small class="text-muted">
            Every euro of net revenue is assigned to exactly one stream, so the
            streams of a month add up to that month's net revenue.
          </small>
        </div>
        <MethodologyModal title="Revenue streams"
          ><p>
            <strong>Gross revenue</strong> is what buyers pay (the GO value plus
            any fee on their invoice) and is booked as revenue because Soldera
            holds the principal. <strong>Producer payout</strong> is what is
            paid on to producers, net of the commission deducted from it.
            <strong>Net revenue</strong> is the difference: Soldera's own money,
            split into streams by the invoice line that produced it. Streams are
            grouped into:
          </p>
          <dl>
            <dt>GO trading</dt>
            <dd>
              <strong>GO commission</strong> (FEE_SOLDERA_COMMISSION charged to
              producers, plus the predicted commission on sales that are not yet
              invoiced — shown as pending),
              <strong>GO trading margin</strong> (buyer price minus producer
              payout on brokered or swap deals) and
              <strong>standalone GO sales</strong> (buyer GO invoices with no
              producer payout on the platform).
            </dd>
            <dt>Platform fees</dt>
            <dd>
              Cancellation, procurement, issuance, transfer, flat (subscription)
              and other Soldera fee lines.
            </dd>
            <dt>One-offs</dt>
            <dd>
              FEE_SOLDERA_SERVICE lines: ad-hoc service or consulting work that
              does not recur.
            </dd>
            <dt>Corrections</dt>
            <dd>
              EXPENSE_CORRECTION payouts and negative fee lines. Stored
              positive, subtracted from net revenue.
            </dd>
          </dl>
          <p class="mb-1">
            <strong>Settled vs pending.</strong> A stream amount is settled once
            the invoice carrying it is paid; predicted commission and unpaid
            invoices are pending. Registry pass-through fees (collected and
            forwarded 1:1) and referral payouts are not streams: they never
            change net revenue.
          </p>
          <p class="mb-0 text-muted small">
            Backend: <code>RevenueStream</code> in
            <code>app/money/admin/financial_dashboard_service.py</code>; the
            <code>streams_settled</code> / <code>streams_pending</code> maps on
            every monthly row.
          </p>
        </MethodologyModal>
      </div>
    </div>
    <!-- Gross vs net for the period -->
    <div class="col-12">
      <div class="row g-3">
        <div class="col-md-4">
          <div class="card h-100 border-success">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">
                GO gross revenue (GMV)
              </h6>
              <h3 class="card-title text-success mb-0">
                {{ formatMoney(summary.go_revenue_settled) }} €
              </h3>
              <small class="text-muted"
                >Buyer payments for certificates, booked as revenue.
                Settled.</small
              >
              <div
                v-if="summary.go_revenue_pending !== 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-success fst-italic"
                  >+ {{ formatMoney(summary.go_revenue_pending) }} €
                  pending</small
                >
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100 border-danger">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">Producer payout</h6>
              <h3 class="card-title text-danger mb-0">
                {{ formatMoney(summary.costs_paid) }} €
                <span
                  v-if="summary.gross_revenue_settled > 0"
                  class="fs-6 text-muted fw-normal"
                  >({{
                    pct(summary.costs_paid, summary.gross_revenue_settled)
                  }}%)</span
                >
              </h3>
              <small class="text-muted"
                >Net payout to producers (after commission) plus corrections.
                Paid.</small
              >
              <div
                v-if="summary.costs_pending > 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-danger fst-italic"
                  >+ {{ formatMoney(summary.costs_pending) }} € pending</small
                >
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100 border-info">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">Net revenue</h6>
              <h3 class="card-title text-info mb-0">
                {{ formatMoney(summary.net_revenue_settled) }} €
                <span
                  v-if="summary.gross_revenue_settled > 0"
                  class="fs-6 text-muted fw-normal"
                  >({{
                    pct(
                      summary.net_revenue_settled,
                      summary.gross_revenue_settled
                    )
                  }}% take rate)</span
                >
              </h3>
              <small class="text-muted"
                >Gross minus payout: what Soldera keeps, split into the streams
                below.</small
              >
              <div
                v-if="summary.net_revenue_pending !== 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="text-info fst-italic"
                  >{{ summary.net_revenue_pending > 0 ? "+" : ""
                  }}{{ formatMoney(summary.net_revenue_pending) }} €
                  pending</small
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">Gross → net, per month</h5>
          <p class="text-muted small mb-2">
            Gross revenue splits into the producer payout and Soldera's net
            revenue. Settled only; click a month to open it.
          </p>
          <div class="chart-lg">
            <canvas ref="grossNetChartRef"></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <h6 class="text-muted mb-0">Net revenue by source</h6>
    </div>
    <!-- Period totals per stream group (net revenue) -->
    <div class="col-12">
      <div class="row g-3">
        <div
          v-for="group in STREAM_GROUP_ORDER"
          :key="group"
          class="col-md-6 col-lg-3"
        >
          <div class="card h-100" :class="groupBorderClass(group)">
            <div class="card-body">
              <h6 class="card-subtitle mb-2 text-muted">
                {{ STREAM_GROUP_LABELS[group] }}
              </h6>
              <h3 class="card-title mb-0" :class="groupTextClass(group)">
                {{ group === "adjustment" ? "-" : ""
                }}{{
                  formatMoney(
                    groupTotal(summary.streams_settled, streams, group)
                  )
                }}
                €
                <span
                  v-if="group !== 'adjustment' && positiveSettled > 0"
                  class="fs-6 text-muted fw-normal"
                >
                  ({{
                    pct(
                      groupTotal(summary.streams_settled, streams, group),
                      positiveSettled
                    )
                  }}%)
                </span>
              </h3>
              <small class="text-muted">Settled (invoices paid)</small>
              <div
                v-if="groupTotal(summary.streams_pending, streams, group) !== 0"
                class="mt-2 pt-2 border-top"
              >
                <small class="fst-italic" :class="groupTextClass(group)">
                  {{ group === "adjustment" ? "-" : "+" }}
                  {{
                    formatMoney(
                      groupTotal(summary.streams_pending, streams, group)
                    )
                  }}
                  € pending
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg-7">
      <div class="card h-100">
        <div class="card-body">
          <h5 class="card-title">Net revenue by group, per month</h5>
          <p class="text-muted small mb-2">
            Solid = settled, faded = pending. Click a month to open its
            breakdown.
          </p>
          <div class="chart-lg">
            <canvas ref="groupChartRef"></canvas>
          </div>
        </div>
      </div>
    </div>
    <div class="col-lg-5">
      <div class="card h-100">
        <div class="card-body">
          <h5 class="card-title">Platform fees by type</h5>
          <p class="text-muted small mb-2">Settled + pending per fee type.</p>
          <div class="chart-lg">
            <canvas ref="feesChartRef"></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <div class="card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="card-title mb-0">Monthly streams</h5>
            <div class="form-check form-switch mb-0">
              <input
                id="mixIncludePending"
                v-model="includePending"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label small" for="mixIncludePending"
                >Include pending</label
              >
            </div>
          </div>
          <DataTable
            :value="tableRows"
            sortField="period"
            :sortOrder="-1"
            class="p-datatable-sm border rounded"
            :rowClass="clickableRow"
            @row-click="(event) => $emit('openMonth', event.data.source)"
          >
            <Column field="period" header="Period" sortable />
            <Column field="go" header="GO trading" sortable>
              <template #body="{ data }">
                <span class="text-primary fw-semibold"
                  >{{ formatMoney(data.go) }} €</span
                >
                <div class="small text-muted stream-detail">
                  <span
                    v-for="stream in streamsInGroup(streams, 'go')"
                    :key="stream.key"
                    class="me-2"
                  >
                    {{ shortLabel(stream) }}
                    {{ formatMoney(data.streams[stream.key]) }}
                  </span>
                </div>
              </template>
            </Column>
            <Column field="platform_fee" header="Platform fees" sortable>
              <template #body="{ data }">
                <span class="text-success fw-semibold"
                  >{{ formatMoney(data.platform_fee) }} €</span
                >
                <div class="small text-muted stream-detail">
                  <span
                    v-for="stream in nonZeroStreams(
                      data.streams,
                      'platform_fee'
                    )"
                    :key="stream.key"
                    class="me-2"
                  >
                    {{ shortLabel(stream) }}
                    {{ formatMoney(data.streams[stream.key]) }}
                  </span>
                </div>
              </template>
            </Column>
            <Column field="one_off" header="One-offs" sortable>
              <template #body="{ data }">
                <span v-if="data.one_off" class="text-warning-emphasis"
                  >{{ formatMoney(data.one_off) }} €</span
                >
                <span v-else class="text-muted">-</span>
              </template>
            </Column>
            <Column field="adjustment" header="Corrections" sortable>
              <template #body="{ data }">
                <span v-if="data.adjustment" class="text-danger"
                  >-{{ formatMoney(data.adjustment) }} €</span
                >
                <span v-else class="text-muted">-</span>
              </template> </Column
            ><Column field="gross" header="Gross revenue" sortable>
              <template #body="{ data }">
                <span class="text-success"
                  >{{ formatMoney(data.gross) }} €</span
                >
                <div class="small text-muted">
                  payout {{ formatMoney(data.payout) }}
                </div>
              </template>
            </Column>
            <Column field="net" header="Net revenue" sortable
              ><template #body="{ data }">
                <span
                  class="fw-bold"
                  :class="data.net >= 0 ? 'text-info' : 'text-danger'"
                  >{{ formatMoney(data.net) }} €</span
                >
              </template> </Column
            ><Column field="takeRate" header="Take rate" sortable>
              <template #body="{ data }">
                <span v-if="data.gross > 0"
                  >{{ (data.takeRate * 100).toFixed(1) }}%</span
                >
                <span v-else class="text-muted">-</span>
              </template>
            </Column>
            <Column field="goShare" header="GO share" sortable
              ><template #body="{ data }">
                <span v-if="data.goShare > 0"
                  >{{ (data.goShare * 100).toFixed(1) }}%</span
                >
                <span v-else class="text-muted">-</span>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatMoney } from "@/composables/formatUtils";
import type { ChartConfiguration } from "chart.js";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import MethodologyModal from "./MethodologyModal.vue";
import {
  STREAM_GROUP_COLORS,
  STREAM_GROUP_LABELS,
  STREAM_GROUP_ORDER,
  groupTextClass,
  groupTotal,
  moneyTooltip,
  netFromStreams,
  pct,
  periodLabel,
  streamsInGroup,
} from "./financialDashboardMetrics";
import type {
  MonthlyData,
  RevenueStreamGroup,
  RevenueStreamInfo,
  StreamTotals,
  Summary,
} from "./financialDashboardTypes";
import { useChartCanvas } from "./useChartCanvas";

interface MixRow {
  period: string;
  source: MonthlyData;
  streams: StreamTotals;
  go: number;
  platform_fee: number;
  one_off: number;
  adjustment: number;
  net: number;
  /** Gross revenue (buyer payments + fees) and producer payout on the same settled/pending basis. */
  gross: number;
  payout: number;
  takeRate: number;
  /** GO trading as a fraction of all revenue-generating streams (before corrections). */
  goShare: number;
}
const props = defineProps<{
  monthly: MonthlyData[];
  summary: Summary;
  streams: RevenueStreamInfo[];
}>();

// Stable reference: an inline arrow in the template is a new prop every render and makes
// PrimeVue's DataTable re-render itself endlessly.
const clickableRow = () => "cursor-pointer";

const emit = defineEmits<{
  openMonth: [month: MonthlyData];
}>();

const includePending = ref(false);
const grossNetChartRef = useTemplateRef<HTMLCanvasElement>("grossNetChartRef");
const grossNetChart = useChartCanvas(grossNetChartRef);
const groupChartRef = useTemplateRef<HTMLCanvasElement>("groupChartRef");
const feesChartRef = useTemplateRef<HTMLCanvasElement>("feesChartRef");
const groupChart = useChartCanvas(groupChartRef);
const feesChart = useChartCanvas(feesChartRef);

/** Sum of the revenue-generating groups (before corrections): the base for share percentages. */
const positiveSettled = computed(() =>
  positiveTotal(props.summary.streams_settled)
);

const positiveTotal = (totals: StreamTotals): number =>
  groupTotal(totals, props.streams, "go") +
  groupTotal(totals, props.streams, "platform_fee") +
  groupTotal(totals, props.streams, "one_off");

const combinedStreams = (month: MonthlyData): StreamTotals => {
  const totals = { ...month.streams_settled };
  if (includePending.value) {
    for (const stream of props.streams) {
      totals[stream.key] =
        (totals[stream.key] ?? 0) + (month.streams_pending[stream.key] ?? 0);
    }
  }
  return totals;
};

const tableRows = computed<MixRow[]>(() =>
  props.monthly.map((month) => {
    const streams = combinedStreams(month);
    const go = groupTotal(streams, props.streams, "go");
    const net = netFromStreams(streams);
    return {
      period: periodLabel(month.year, month.month),
      source: month,
      streams,
      go,
      platform_fee: groupTotal(streams, props.streams, "platform_fee"),
      one_off: groupTotal(streams, props.streams, "one_off"),
      adjustment: groupTotal(streams, props.streams, "adjustment"),
      net,
      gross: grossOf(month),
      payout: includePending.value
        ? month.costs_paid + month.costs_pending
        : month.costs_paid,
      takeRate: grossOf(month) > 0 ? net / grossOf(month) : 0,
      goShare: positiveTotal(streams) > 0 ? go / positiveTotal(streams) : 0,
    };
  })
);

const grossOf = (month: MonthlyData): number =>
  includePending.value ? month.gross_revenue : month.gross_revenue_settled;

const nonZeroStreams = (streams: StreamTotals, group: RevenueStreamGroup) =>
  streamsInGroup(props.streams, group).filter(
    (stream) => streams[stream.key] !== 0
  );

const shortLabel = (stream: RevenueStreamInfo): string =>
  stream.label
    .replace(/ fees?$/i, "")
    .replace(/^GO /, "")
    .replace(" (subscription)", "");

const groupBorderClass = (group: RevenueStreamGroup): string =>
  ({
    go: "border-primary",
    platform_fee: "border-success",
    one_off: "border-warning",
    adjustment: "border-danger",
  })[group];

function grossNetChartConfig(): ChartConfiguration {
  const labels = props.monthly.map((month) =>
    periodLabel(month.year, month.month)
  );
  return {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          type: "line",
          label: "Gross revenue (settled)",
          data: props.monthly.map((month) => month.gross_revenue_settled),
          borderColor: "#198754",
          backgroundColor: "#198754",
          tension: 0.25,
          pointRadius: 3,
          order: 0,
        },
        {
          label: "Producer payout (paid)",
          data: props.monthly.map((month) => month.costs_paid),
          backgroundColor: "rgba(220, 53, 69, 0.7)",
          stack: "g",
          order: 1,
        },
        {
          label: "Net revenue (settled)",
          data: props.monthly.map((month) => month.net_revenue_settled),
          backgroundColor: "rgba(13, 202, 240, 0.8)",
          stack: "g",
          order: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "EUR" } },
      },
      plugins: {
        legend: { position: "top", labels: { boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (ctx) => moneyTooltip(ctx.dataset.label, ctx.parsed.y ?? 0),
          },
        },
      },
      onClick: (_event, elements) => {
        if (elements.length > 0)
          emit("openMonth", props.monthly[elements[0].index]);
      },
    },
  };
}

function groupChartConfig(): ChartConfiguration {
  const labels = props.monthly.map((month) =>
    periodLabel(month.year, month.month)
  );
  const sign = (group: RevenueStreamGroup) => (group === "adjustment" ? -1 : 1);
  const datasets = STREAM_GROUP_ORDER.flatMap((group) => [
    {
      label: `${STREAM_GROUP_LABELS[group]} (settled)`,
      data: props.monthly.map(
        (month) =>
          sign(group) * groupTotal(month.streams_settled, props.streams, group)
      ),
      backgroundColor: STREAM_GROUP_COLORS[group].solid,
      stack: "net",
    },
    {
      label: `${STREAM_GROUP_LABELS[group]} (pending)`,
      data: props.monthly.map(
        (month) =>
          sign(group) * groupTotal(month.streams_pending, props.streams, group)
      ),
      backgroundColor: STREAM_GROUP_COLORS[group].faded,
      borderColor: STREAM_GROUP_COLORS[group].border,
      borderWidth: 1,
      stack: "net",
    },
  ]);
  return {
    type: "bar",
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "EUR" } },
      },
      plugins: {
        legend: { position: "top", labels: { boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (ctx) => moneyTooltip(ctx.dataset.label, ctx.parsed.y ?? 0),
          },
        },
      },
      onClick: (_event, elements) => {
        if (elements.length > 0)
          emit("openMonth", props.monthly[elements[0].index]);
      },
    },
  };
}

function feesChartConfig(): ChartConfiguration {
  const labels = props.monthly.map((month) =>
    periodLabel(month.year, month.month)
  );
  const palette = [
    "#198754",
    "#20c997",
    "#0dcaf0",
    "#6f42c1",
    "#fd7e14",
    "#6c757d",
  ];
  const datasets = streamsInGroup(props.streams, "platform_fee").map(
    (stream, index) => ({
      label: stream.label,
      data: props.monthly.map(
        (month) =>
          (month.streams_settled[stream.key] ?? 0) +
          (month.streams_pending[stream.key] ?? 0)
      ),
      backgroundColor: palette[index % palette.length],
      stack: "fees",
    })
  );
  return {
    type: "bar",
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "EUR" } },
      },
      plugins: {
        legend: { position: "top", labels: { boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (ctx) => moneyTooltip(ctx.dataset.label, ctx.parsed.y ?? 0),
          },
        },
      },
    },
  };
}

function renderCharts() {
  grossNetChart.render(grossNetChartConfig());
  groupChart.render(groupChartConfig());
  feesChart.render(feesChartConfig());
}

onMounted(renderCharts);
watch(() => [props.monthly, props.streams], renderCharts, { flush: "post" });
</script>

<style scoped lang="scss">
.chart-lg {
  height: 360px;
}

.stream-detail {
  white-space: normal;
  max-width: 260px;
}

:deep(.cursor-pointer) {
  cursor: pointer;
}
</style>
